import React, { useState, useEffect } from "react";
import { useLocation, useParams } from "react-router-dom";
import SubPageLayout from "../../../components/shared/SubPageLayout";
import Modal from "../../../components/shared/Modal";

import logger from '../../../utils/logger';

// Error Message Component
const ErrorMessage = ({ message }) => (
  <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-4">
    <div className="flex">
      <div className="flex-shrink-0">
        <svg
          className="h-5 w-5 text-red-400"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fillRule="evenodd"
            d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
            clipRule="evenodd"
          />
        </svg>
      </div>
      <div className="ml-3">
        <p className="text-sm text-red-700">{message}</p>
      </div>
    </div>
  </div>
);

// Loading Spinner Component
const LoadingSpinner = () => (
  <div className="flex justify-center items-center py-12">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#202EC6]"></div>
  </div>
);

// Advisor Dashboard Component
const AdvisorDashboard = ({ handleScheduleReview }) => {
  return (
    <div className="space-y-6">
      <div className="bg-surface-default dark:bg-surface-default rounded-lg shadow-md dark:shadow-none p-6">
        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
          Client Insurance Overview
        </h3>
        <div className="space-y-4">
          {SAMPLE_CLIENTS.map((client) => (
            <div key={client.id} className="border rounded-lg p-4">
              <div className="flex justify-between items-start">
                <div>
                  <h4 className="text-base font-medium text-gray-900 dark:text-gray-100">
                    {client.name}
                  </h4>
                  <p className="text-sm text-gray-500 dark:text-gray-500">
                    Insurance Score: {client.score}
                  </p>
                  <p
                    className={`text-sm ${
                      client.status === "Optimized"
                        ? "text-green-600"
                        : "text-yellow-600"
                    }`}
                  >
                    Status: {client.status}
                  </p>
                </div>
                <button
                  className="px-3 py-1 text-sm text-[#202EC6] hover:bg-blue-50 rounded-md"
                  onClick={() => handleScheduleReview(client.id, new Date())}
                >
                  Schedule Review
                </button>
              </div>

              <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                {Object.entries(client.policies).map(([type, policy]) => (
                  <div key={type} className="bg-gray-50 rounded p-3">
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100 capitalize">
                      {type}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{policy.provider}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-500">
                      {policy.coverage || policy.type}
                    </p>
                  </div>
                ))}
              </div>

              {client.recommendations.length > 0 && (
                <div className="mt-4">
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    Recommendations:
                  </p>
                  <ul className="mt-2 list-disc list-inside text-sm text-gray-600 dark:text-gray-400">
                    {client.recommendations.map((rec, idx) => (
                      <li key={idx}>{rec}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Application Modal Component
const ApplicationModal = ({ isOpen, onClose, productType, client }) => {
  const [step, setStep] = useState(1);
  const product = INSURANCE_PRODUCTS.find((p) => p.title === productType);
  const [formData, setFormData] = useState({
    // Personal Information
    firstName: client?.name.split(" ")[0] || "",
    lastName: client?.name.split(" ")[1] || "",
    email: "",
    phone: "",
    dateOfBirth: "",
    ssn: "",
    address: "",
    city: "",
    state: "",
    zipCode: "",

    // Coverage Details
    coverageAmount: "",
    coverageType: "",
    term: "",
    paymentFrequency: "monthly",

    // Health Information (for life/health products)
    height: "",
    weight: "",
    smoker: false,
    medicalConditions: [],
    medications: [],

    // Beneficiary Information
    beneficiaries: [],

    // Financial Information
    annualIncome: "",
    netWorth: "",
    existingCoverage: false,
    replacementPolicy: false,

    // Additional Details
    occupation: "",
    employerName: "",
    hobbies: "",
  });

  if (!isOpen) return null;

  const steps = [
    { id: 1, title: "Personal Information" },
    { id: 2, title: "Coverage Details" },
    {
      id: 3,
      title:
        productType.includes("Life") || productType.includes("Health")
          ? "Health Information"
          : "Additional Details",
    },
    { id: 4, title: "Beneficiary Information" },
    { id: 5, title: "Review & Submit" },
  ];

  const renderStepContent = () => {
    const inputClasses = "mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 focus:ring-[#202EC6] focus:border-[#202EC6] bg-white dark:bg-surface-raised text-gray-900 dark:text-gray-100";
    const labelClasses = "block text-sm font-medium text-gray-700 dark:text-gray-300";
    const selectClasses = "mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 focus:ring-[#202EC6] focus:border-[#202EC6] bg-white dark:bg-surface-raised text-gray-900 dark:text-gray-100";

    switch (step) {
      case 1:
        return (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelClasses}>First Name</label>
              <input
                type="text"
                value={formData.firstName}
                onChange={(e) =>
                  setFormData({ ...formData, firstName: e.target.value })
                }
                className={inputClasses}
              />
            </div>
            <div>
              <label className={labelClasses}>Last Name</label>
              <input
                type="text"
                value={formData.lastName}
                onChange={(e) =>
                  setFormData({ ...formData, lastName: e.target.value })
                }
                className={inputClasses}
              />
            </div>
            <div>
              <label className={labelClasses}>Date of Birth</label>
              <input
                type="date"
                value={formData.dateOfBirth}
                onChange={(e) =>
                  setFormData({ ...formData, dateOfBirth: e.target.value })
                }
                className={inputClasses}
              />
            </div>
            <div>
              <label className={labelClasses}>SSN</label>
              <input
                type="password"
                value={formData.ssn}
                onChange={(e) =>
                  setFormData({ ...formData, ssn: e.target.value })
                }
                className={inputClasses}
                placeholder="XXX-XX-XXXX"
              />
            </div>
            <div className="col-span-2">
              <label className={labelClasses}>Email</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) =>
                  setFormData({ ...formData, email: e.target.value })
                }
                className={inputClasses}
              />
            </div>
            <div className="col-span-2">
              <label className={labelClasses}>Phone</label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) =>
                  setFormData({ ...formData, phone: e.target.value })
                }
                className={inputClasses}
              />
            </div>
            <div className="col-span-2">
              <label className={labelClasses}>Address</label>
              <input
                type="text"
                value={formData.address}
                onChange={(e) =>
                  setFormData({ ...formData, address: e.target.value })
                }
                className={inputClasses}
              />
            </div>
            <div>
              <label className={labelClasses}>City</label>
              <input
                type="text"
                value={formData.city}
                onChange={(e) =>
                  setFormData({ ...formData, city: e.target.value })
                }
                className={inputClasses}
              />
            </div>
            <div>
              <label className={labelClasses}>State</label>
              <input
                type="text"
                value={formData.state}
                onChange={(e) =>
                  setFormData({ ...formData, state: e.target.value })
                }
                className={inputClasses}
              />
            </div>
            <div>
              <label className={labelClasses}>ZIP Code</label>
              <input
                type="text"
                value={formData.zipCode}
                onChange={(e) =>
                  setFormData({ ...formData, zipCode: e.target.value })
                }
                className={inputClasses}
              />
            </div>
          </div>
        );
      case 2:
        return (
          <div className="space-y-4">
            <div>
              <label className={labelClasses}>Coverage Amount</label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <span className="text-gray-500 dark:text-gray-400 sm:text-sm">$</span>
                </div>
                <input
                  type="text"
                  value={formData.coverageAmount}
                  onChange={(e) =>
                    setFormData({ ...formData, coverageAmount: e.target.value })
                  }
                  className={`${inputClasses} pl-7 pr-12`}
                  placeholder="0.00"
                />
              </div>
            </div>
            <div>
              <label className={labelClasses}>Coverage Type</label>
              <select
                value={formData.coverageType}
                onChange={(e) =>
                  setFormData({ ...formData, coverageType: e.target.value })
                }
                className={selectClasses}
              >
                {product?.features.map((feature, idx) => (
                  <option key={idx} value={feature}>
                    {feature}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelClasses}>Payment Frequency</label>
              <select
                value={formData.paymentFrequency}
                onChange={(e) =>
                  setFormData({ ...formData, paymentFrequency: e.target.value })
                }
                className={selectClasses}
              >
                <option value="monthly">Monthly</option>
                <option value="quarterly">Quarterly</option>
                <option value="semiannual">Semi-Annual</option>
                <option value="annual">Annual</option>
              </select>
            </div>
          </div>
        );
      case 3:
        return productType.includes("Life") || productType.includes("Health") ? (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={labelClasses}>Height</label>
                <input
                  type="text"
                  value={formData.height}
                  onChange={(e) =>
                    setFormData({ ...formData, height: e.target.value })
                  }
                  className={inputClasses}
                  placeholder="5'10''"
                />
              </div>
              <div>
                <label className={labelClasses}>Weight</label>
                <input
                  type="text"
                  value={formData.weight}
                  onChange={(e) =>
                    setFormData({ ...formData, weight: e.target.value })
                  }
                  className={inputClasses}
                  placeholder="lbs"
                />
              </div>
            </div>
            <div>
              <label className={labelClasses}>Tobacco Use</label>
              <div className="mt-2">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.smoker}
                    onChange={(e) =>
                      setFormData({ ...formData, smoker: e.target.checked })
                    }
                    className="h-4 w-4 text-[#202EC6] focus:ring-[#202EC6] border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-surface-raised"
                  />
                  <label className="ml-2 block text-sm text-gray-900 dark:text-gray-100">
                    Have you used tobacco products in the last 12 months?
                  </label>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <label className={labelClasses}>Occupation</label>
              <input
                type="text"
                value={formData.occupation}
                onChange={(e) =>
                  setFormData({ ...formData, occupation: e.target.value })
                }
                className={inputClasses}
              />
            </div>
            <div>
              <label className={labelClasses}>Employer Name</label>
              <input
                type="text"
                value={formData.employerName}
                onChange={(e) =>
                  setFormData({ ...formData, employerName: e.target.value })
                }
                className={inputClasses}
              />
            </div>
          </div>
        );
      case 4:
        return (
          <div className="space-y-4">
            <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
              <h3 className="text-lg font-medium text-blue-900 dark:text-blue-100">
                Beneficiary Information
              </h3>
              <p className="mt-2 text-sm text-blue-700 dark:text-blue-300">
                Add primary and contingent beneficiaries for your policy.
              </p>
            </div>
            {formData.beneficiaries.map((beneficiary, index) => (
              <div key={index} className="border dark:border-gray-700 rounded-lg p-4">
                {/* Beneficiary form fields */}
              </div>
            ))}
            <button
              type="button"
              onClick={() =>
                setFormData({
                  ...formData,
                  beneficiaries: [
                    ...formData.beneficiaries,
                    { name: "", relationship: "", percentage: "" },
                  ],
                })
              }
              className="mt-2 px-4 py-2 border border-[#202EC6] text-[#202EC6] dark:border-[#60A5FA] dark:text-[#60A5FA] rounded-md hover:bg-blue-50 dark:hover:bg-blue-900/20"
            >
              Add Beneficiary
            </button>
          </div>
        );
      case 5:
        return (
          <div className="space-y-6">
            <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
              <h3 className="text-lg font-medium text-blue-900 dark:text-blue-100">
                Review Your Application
              </h3>
              <p className="mt-2 text-sm text-blue-700 dark:text-blue-300">
                Please review your application details before submitting.
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <h4 className="font-medium text-gray-900 dark:text-gray-100">
                  Personal Information
                </h4>
                <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                  <p>
                    {formData.firstName} {formData.lastName}
                  </p>
                  <p>{formData.email}</p>
                  <p>{formData.phone}</p>
                  <p>{formData.address}</p>
                  <p>
                    {formData.city}, {formData.state} {formData.zipCode}
                  </p>
                </div>
              </div>

              <div>
                <h4 className="font-medium text-gray-900 dark:text-gray-100">Coverage Details</h4>
                <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                  <p>Coverage Amount: ${formData.coverageAmount}</p>
                  <p>Coverage Type: {formData.coverageType}</p>
                  <p>Payment Frequency: {formData.paymentFrequency}</p>
                </div>
              </div>

              {(productType.includes("Life") ||
                productType.includes("Health")) && (
                <div>
                  <h4 className="font-medium text-gray-900 dark:text-gray-100">
                    Health Information
                  </h4>
                  <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                    <p>Height: {formData.height}</p>
                    <p>Weight: {formData.weight}</p>
                    <p>Tobacco Use: {formData.smoker ? "Yes" : "No"}</p>
                  </div>
                </div>
              )}

              <div>
                <h4 className="font-medium text-gray-900 dark:text-gray-100">Beneficiaries</h4>
                <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                  {formData.beneficiaries.map((beneficiary, index) => (
                    <div key={index}>
                      <p>Name: {beneficiary.name}</p>
                      <p>Relationship: {beneficiary.relationship}</p>
                      <p>Percentage: {beneficiary.percentage}%</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose} 
      width="full"    // Use standardized full size for complex insurance forms
    >
      <div className="p-6 border-b border-gray-200 dark:border-gray-700">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              Insurance Application
            </h2>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
              Complete your application for {productType}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500 dark:text-gray-500 dark:hover:text-gray-400"
          >
            <svg
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <div className="mb-8">
          <div className="flex items-center justify-between">
            {steps.map((s, index) => (
              <div
                key={s.id}
                className={`flex items-center ${index < steps.length - 1 ? "flex-1" : ""}`}
              >
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    step >= s.id
                      ? "bg-[#202EC6] text-white"
                      : "bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400"
                  }`}
                >
                  {s.id}
                </div>
                {index < steps.length - 1 && (
                  <div
                    className={`flex-1 h-1 mx-2 ${
                      step > s.id 
                        ? "bg-[#202EC6]" 
                        : "bg-gray-200 dark:bg-gray-700"
                    }`}
                  />
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-between mt-2">
            {steps.map((s) => (
              <span key={s.id} className="text-xs text-gray-500 dark:text-gray-400">
                {s.title}
              </span>
            ))}
          </div>
        </div>

        <form className="space-y-6">
          {renderStepContent()}

          <div className="flex justify-between pt-6 border-t border-gray-200 dark:border-gray-700">
            {step > 1 && (
              <button
                type="button"
                onClick={() => setStep(step - 1)}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100"
              >
                Back
              </button>
            )}
            <button
              type="button"
              onClick={() => {
                if (step === steps.length) {
                  // Handle form submission
                  logger.info("Submitting application:", formData);
                  onClose();
                } else {
                  setStep(step + 1);
                }
              }}
              className="px-4 py-2 bg-[#202EC6] text-white rounded-md hover:bg-opacity-90"
            >
              {step === steps.length ? "Submit Application" : "Next"}
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

// API integration points (to be implemented)
const API = {
  getInsuranceProducts: async () => {
    // TODO: Implement API call to fetch insurance products
    return Promise.resolve([]);
  },
  getClients: async () => {
    // TODO: Implement API call to fetch client list
    return Promise.resolve(SAMPLE_CLIENTS);
  },
  getClientDetails: async (clientId) => {
    // TODO: Implement API call to fetch client details
    return Promise.resolve(SAMPLE_CLIENTS.find((c) => c.id === clientId));
  },
  getRates: async (productType) => {
    // TODO: Implement API call to fetch rates from Annuity Rate Watch
    return Promise.resolve([]);
  },
  initiateApplication: async (productType, clientId) => {
    // TODO: Implement API call to initiate FireLight application
    return Promise.resolve({ applicationId: "test-123" });
  },
  scheduleReview: async (clientId, date) => {
    // TODO: Implement API call to schedule client review
    return Promise.resolve({ success: true });
  },
};

// Sample insurance providers data
const INSURANCE_PROVIDERS = {
  life: ["Nationwide", "Pacific Life", "TransAmerica"],
  health: ["Blue Cross Blue Shield", "Humana", "Kaiser"],
  disability: ["TransAmerica", "State Farm", "Allstate"],
  longTerm: ["Nationwide", "Pacific Life", "GEICO"],
  property: ["GEICO", "State Farm", "Progressive"],
  liability: ["Progressive", "Allstate", "State Farm"],
};

// Insurance products data
const INSURANCE_PRODUCTS = [
  {
    title: "Life Insurance & Annuities",
    description:
      "Protect your loved ones' financial future with comprehensive life insurance coverage and secure retirement with annuity options.",
    providers: INSURANCE_PROVIDERS.life,
    features: [
      "Term and permanent coverage options",
      "Fixed and variable annuities",
      "Optional riders for customization",
      "Death benefit protection",
    ],
    integrations: {
      fireLight: false,
      rateWatch: true,
      applicationType: "standard",
    },
  },
  {
    title: "Health Insurance",
    description:
      "Access quality healthcare with our range of health insurance plans.",
    providers: INSURANCE_PROVIDERS.health,
    features: [
      "Comprehensive medical coverage",
      "Prescription drug benefits",
      "Preventive care services",
      "Network provider access",
    ],
    integrations: {
      fireLight: false,
      rateWatch: true,
      applicationType: "ehealthinsurance",
    },
  },
  {
    title: "Disability Insurance",
    description: "Secure your income with disability insurance coverage.",
    providers: INSURANCE_PROVIDERS.disability,
    features: [
      "Short-term and long-term options",
      "Income replacement benefits",
      "Own-occupation coverage",
      "Flexible elimination periods",
    ],
    integrations: {
      fireLight: false,
      rateWatch: true,
      applicationType: "standard",
    },
  },
  {
    title: "Long-term Care Insurance",
    description:
      "Plan for future care needs with long-term care insurance options.",
    providers: INSURANCE_PROVIDERS.longTerm,
    features: [
      "Customizable care options",
      "Inflation protection",
      "Facility and home care coverage",
      "Shared care benefits",
    ],
    integrations: {
      fireLight: true,
      rateWatch: true,
      applicationType: "firelight",
    },
  },
  {
    title: "Property & Casualty",
    description:
      "Protect your assets with comprehensive property and casualty insurance coverage.",
    providers: INSURANCE_PROVIDERS.property,
    features: [
      "Home and auto coverage",
      "Personal property protection",
      "Liability coverage",
      "Multi-policy discounts",
    ],
    integrations: {
      fireLight: false,
      rateWatch: true,
      applicationType: "comparative",
    },
  },
  {
    title: "Liability & Commercial Insurance",
    description:
      "Safeguard your business with comprehensive liability and commercial insurance solutions.",
    providers: INSURANCE_PROVIDERS.liability,
    features: [
      "Professional liability coverage",
      "Business owner's policies",
      "Commercial property protection",
      "Workers compensation",
    ],
    integrations: {
      fireLight: false,
      rateWatch: true,
      applicationType: "comparative",
    },
  },
];

// Sample client data
const SAMPLE_CLIENTS = [
  {
    id: 1,
    name: "John Smith",
    score: 75,
    status: "Underinsured",
    policies: {
      life: { provider: "Nationwide", coverage: "$500,000" },
      health: { provider: "BCBS", type: "PPO" },
      property: { provider: "GEICO", type: "Home & Auto Bundle" },
    },
    recommendations: [
      "Review Life Insurance Coverage",
      "Consider adding disability insurance",
    ],
  },
  {
    id: 2,
    name: "Sarah Johnson",
    score: 92,
    status: "Optimized",
    policies: {
      life: { provider: "Pacific Life", coverage: "$1,000,000" },
      health: { provider: "Humana", type: "PPO" },
      property: { provider: "Progressive", type: "Home Insurance" },
    },
    recommendations: ["Annual policy review due", "Consider umbrella policy"],
  },
];

const Insurance = () => {
  const [view, setView] = useState("marketplace");
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showApplicationModal, setShowApplicationModal] = useState(false);
  const [showRateCompare, setShowRateCompare] = useState(false);

  // Data state
  const [clients, setClients] = useState([]);
  const [products, setProducts] = useState([]);
  const [rates, setRates] = useState({});

  // Get location and params
  const location = useLocation();
  const { householdId } = useParams();
  const isAdvisorWorkflow = location.pathname.includes("/advisor-portal");

  // Handle rate comparison
  const handleCompareRates = async (productType) => {
    if (!productType) return;

    setSelectedProduct(productType);
    setLoading(true);
    try {
      const rateData = await API.getRates(productType);
      setRates((prev) => ({ ...prev, [productType]: rateData }));
      setShowRateCompare(true);
    } catch (err) {
      setError("Failed to load rate comparison");
      logger.error("Error loading rates:", err);
    } finally {
      setLoading(false);
    }
  };

  // Handle scheduling a review
  const handleScheduleReview = async (clientId, date) => {
    setLoading(true);
    try {
      await API.scheduleReview(clientId, date);
      // You might want to show a success message or update the UI
    } catch (err) {
      setError("Failed to schedule review");
      logger.error("Error scheduling review:", err);
    } finally {
      setLoading(false);
    }
  };

  // MarketplaceView component
  const MarketplaceView = () => {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {INSURANCE_PRODUCTS.map((product, index) => (
            <div
              key={index}
              className="bg-surface-default dark:bg-surface-default rounded-lg shadow-md dark:shadow-none overflow-hidden hover:shadow-lg dark:shadow-none transition-shadow duration-300"
            >
              <div className="p-6">
                <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
                  {product.title}
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-4">{product.description}</p>

                <div className="space-y-4">
                  <div>
                    <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
                      Available Providers
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {product.providers.map((provider, idx) => (
                        <span
                          key={idx}
                          className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                        >
                          {provider}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
                      Key Features
                    </h4>
                    <ul className="list-disc list-inside text-sm text-gray-600 dark:text-gray-400 space-y-1">
                      {product.features.map((feature, idx) => (
                        <li key={idx}>{feature}</li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="mt-6 flex space-x-4 justify-end">
                  <button
                    onClick={() => {
                      setSelectedProduct(product.title);
                      setShowApplicationModal(true);
                    }}
                    className="px-4 py-2 bg-[#202EC6] text-white rounded-md hover:bg-opacity-90"
                  >
                    Apply Now
                  </button>
                  <button
                    onClick={() => handleCompareRates(product.title)}
                    className="text-[#202EC6] hover:text-opacity-80 text-sm font-medium"
                  >
                    Compare Rates →
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {showApplicationModal && (
          <ApplicationModal
            isOpen={showApplicationModal}
            onClose={() => setShowApplicationModal(false)}
            productType={selectedProduct}
            client={selectedClient}
          />
        )}
      </div>
    );
  };

  useEffect(() => {
    const loadInitialData = async () => {
      setLoading(true);
      try {
        // Load products data
        const productsData = await API.getInsuranceProducts();
        setProducts(
          productsData.length > 0 ? productsData : INSURANCE_PRODUCTS,
        );

        // If we're in the advisor workflow with a householdId, load that specific client
        if (isAdvisorWorkflow && householdId) {
          const clientDetails = await API.getClientDetails(householdId);
          setSelectedClient(clientDetails);
          setView("dashboard");
        } else {
          // For financial planning path, show marketplace view
          setView("marketplace");
          if (isAdvisorWorkflow) {
            // Only load clients if in advisor workflow
            const clientsData = await API.getClients();
            setClients(clientsData);
          }
        }
      } catch (err) {
        setError("Failed to load initial data");
        logger.error("Error loading initial data:", err);
      } finally {
        setLoading(false);
      }
    };

    loadInitialData();
  }, [isAdvisorWorkflow, householdId]);

  return (
    <SubPageLayout
      title={
        isAdvisorWorkflow && selectedClient
          ? `Insurance - ${selectedClient.name}`
          : "Insurance Marketplace"
      }
      breadcrumbItems={[
        { label: "Home", path: "/" },
        ...(isAdvisorWorkflow && householdId
          ? [
              { label: "Advisor Portal", path: "/advisor-portal" },
              { label: "Client Database", path: "/advisor-portal/clients" },
              {
                label: "Household Profile",
                path: `/advisor-portal/household-profile/${householdId}`,
              },
              {
                label: "Insurance",
                path: `/advisor-portal/household-profile/${householdId}/insurance`,
              },
            ]
          : [
              { label: "Financial Planning", path: "/financial-planning" },
              { label: "Insurance", path: "/financial-planning/insurance" },
            ]),
      ]}
      loading={loading}
      error={error}
    >
      {loading ? (
        <LoadingSpinner />
      ) : error ? (
        <ErrorMessage message={error} />
      ) : (
        <>
          {view === "marketplace" && <MarketplaceView />}
          {view === "dashboard" && selectedClient && (
            <AdvisorDashboard
              client={selectedClient}
              handleScheduleReview={handleScheduleReview}
            />
          )}
        </>
      )}
    </SubPageLayout>
  );
};

export default Insurance;
